import type { QueryClient } from '@tanstack/react-query';
import type { OpsActiveTimer } from './opsApi';

/**
 * NOTE (#157 dependency): this key, like ['ops-tasks', 'timer-picker', q], is not scoped by user.
 * Clearing or scoping these caches on sign-out / user change belongs to the session work in #157;
 * until then a different user in the same browser could briefly see the previous user's cached
 * timer within `staleTime`. Server ownership checks (404) still prevent any mutation.
 */
export const OPS_ACTIVE_TIMER_QUERY_KEY = ['ops-active-timer'] as const;

/**
 * How trustworthy the timer shown to the user is.
 * - `loading`: nothing known yet; never treated as idle.
 * - `confirmed`: the latest authoritative read succeeded (a null timer is a confirmed idle).
 * - `stale`: the last read failed but an earlier answer is on screen; it is shown, clearly marked.
 * - `unavailable`: the read failed and nothing is known; never treated as idle.
 */
export type OpsTimerReadStatus = 'loading' | 'confirmed' | 'stale' | 'unavailable';

export interface TimerReadSnapshot {
  isPending: boolean;
  isError: boolean;
  data: OpsActiveTimer | null | undefined;
}

export function deriveTimerReadStatus(read: TimerReadSnapshot): OpsTimerReadStatus {
  const hasAnswer = read.data !== undefined;
  if (read.isError) return hasAnswer ? 'stale' : 'unavailable';
  if (read.isPending || !hasAnswer) return 'loading';
  return 'confirmed';
}

/**
 * Task rows already carry `activeSessionId` from the server; the authoritative active-timer read only
 * fills the gap, so a task list never needs its own active-session request.
 */
export function markActiveTimerTask<T extends { id: string; activeTimerId?: string | null }>(
  tasks: T[],
  activeTimer: OpsActiveTimer | null
): T[] {
  if (!activeTimer?.taskId) return tasks;
  return tasks.map((task) =>
    task.id === activeTimer.taskId && !task.activeTimerId ? { ...task, activeTimerId: activeTimer.id } : task
  );
}

/** A new timer may only be started from a confirmed idle state, never from "no response yet". */
export function canStartTimer(status: OpsTimerReadStatus, activeTimer: OpsActiveTimer | null): boolean {
  return status === 'confirmed' && activeTimer === null;
}

/** A running timer can be finished or switched while its state is confirmed or shown as stale. */
export function canTransitionRunningTimer(status: OpsTimerReadStatus, activeTimer: OpsActiveTimer | null): boolean {
  return activeTimer !== null && (status === 'confirmed' || status === 'stale');
}

export type TimerTransitionIntent =
  /** `submittedAt` is the submit time in server-clock epoch ms (client time plus the known skew). */
  | { kind: 'start'; taskId: string; submittedAt: number }
  | { kind: 'finish'; timerId: string }
  | { kind: 'switch'; timerId: string; taskId: string };

/** Tolerance for residual clock error between the corrected client clock and the server clock. */
export const START_SUBMIT_TOLERANCE_MS = 2_000;

export type TimerTransitionOutcome = 'committed' | 'not-committed' | 'conflict';

/**
 * Compares what a failed or uncertain request intended with the authoritative server state read
 * afterwards, so a lost response is never blindly retried (which could duplicate a start/finish).
 */
export function resolveTransitionOutcome(
  intent: TimerTransitionIntent,
  authoritative: OpsActiveTimer | null
): TimerTransitionOutcome {
  if (intent.kind === 'finish') {
    return authoritative && authoritative.id === intent.timerId ? 'not-committed' : 'committed';
  }
  if (intent.kind === 'switch') {
    if (!authoritative || authoritative.id !== intent.timerId) return 'conflict';
    return (authoritative.taskId || '') === intent.taskId ? 'committed' : 'not-committed';
  }
  if (!authoritative) return 'not-committed';
  if ((authoritative.taskId || '') !== intent.taskId) return 'conflict';
  // A timer for the same task (including general work, '') that began before this request was
  // submitted was started elsewhere; this request never committed.
  const startedAt = Date.parse(authoritative.startedAt);
  if (Number.isNaN(startedAt)) return 'conflict';
  return startedAt >= intent.submittedAt - START_SUBMIT_TOLERANCE_MS ? 'committed' : 'conflict';
}

export function finishNoteDraftKey(timerId: string): string {
  return `ops_finish_note_draft_${timerId}`;
}

/** Everything that depends on timer state; the active-timer read is the authoritative one. */
export function invalidateOpsTimerData(queryClient: QueryClient): Promise<void> {
  const activeTimerRead = queryClient.invalidateQueries({ queryKey: OPS_ACTIVE_TIMER_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ['ops-dashboard'] });
  void queryClient.invalidateQueries({ queryKey: ['ops-tasks'] });
  void queryClient.invalidateQueries({ queryKey: ['ops-task'] });
  void queryClient.invalidateQueries({ queryKey: ['ops-earnings'] });
  // Callers that need the authoritative state before re-enabling controls await this one read only.
  return activeTimerRead;
}

/**
 * Leading + trailing throttle for cross-tab sync messages (including this tab's own echo): the first
 * message invalidates immediately, a burst inside the window yields at most one more invalidation,
 * so a chatty channel can never turn into a request storm.
 */
export function createCoalescedInvalidator(queryClient: QueryClient, windowMs = 500) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let trailing = false;
  const openWindow = () => {
    timer = setTimeout(() => {
      timer = null;
      if (!trailing) return;
      trailing = false;
      invalidateOpsTimerData(queryClient);
      openWindow();
    }, windowMs);
  };
  return {
    schedule() {
      if (timer !== null) {
        trailing = true;
        return;
      }
      invalidateOpsTimerData(queryClient);
      openWindow();
    },
    cancel() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      trailing = false;
    },
  };
}
